import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Card } from '../components/Card';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  Label,
} from 'recharts';
import { mockKeyMetrics, mockChartData } from '../utils/mockData';

const Dashboard: React.FC = () => {
  const [dealType, setDealType] = useState('all');
  const [geography, setGeography] = useState('all');
  const [assetClass, setAssetClass] = useState('all');

  const handleDealTypeChange = (event: SelectChangeEvent) => {
    setDealType(event.target.value);
  };

  const handleGeographyChange = (event: SelectChangeEvent) => {
    setGeography(event.target.value);
  };

  const handleAssetClassChange = (event: SelectChangeEvent) => {
    setAssetClass(event.target.value);
  };

  // Colors for the pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Format large numbers with commas
  const formatNumber = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Get trend icon based on trend value
  const getTrendIcon = (trend: number) => {
    if (trend > 0) {
      return <TrendingUpIcon sx={{ color: 'success.main' }} />;
    } else if (trend < 0) {
      return <TrendingDownIcon sx={{ color: 'error.main' }} />;
    } else {
      return <TrendingFlatIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  // Get trend text color based on trend value
  const getTrendColor = (trend: number) => {
    if (trend > 0) {
      return 'success.main';
    } else if (trend < 0) {
      return 'error.main';
    } else {
      return 'text.secondary';
    }
  };

  // Format trend value as percentage with + or - sign
  const formatTrend = (trend: number) => {
    return `${trend > 0 ? '+' : ''}${trend}%`;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header with filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Investment Dashboard</Typography>
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<DownloadIcon />} 
              sx={{ mr: 1 }}
            >
              Export
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="deal-type-label">Deal Type</InputLabel>
            <Select
              labelId="deal-type-label"
              id="deal-type"
              value={dealType}
              label="Deal Type"
              onChange={handleDealTypeChange}
            >
              <MenuItem value="all">All Deals</MenuItem>
              <MenuItem value="acquisition">Acquisitions</MenuItem>
              <MenuItem value="disposition">Dispositions</MenuItem>
              <MenuItem value="refinance">Refinances</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="geography-label">Geography</InputLabel>
            <Select
              labelId="geography-label"
              id="geography"
              value={geography}
              label="Geography"
              onChange={handleGeographyChange}
            >
              <MenuItem value="all">All Regions</MenuItem>
              <MenuItem value="northeast">Northeast</MenuItem>
              <MenuItem value="midwest">Midwest</MenuItem>
              <MenuItem value="south">South</MenuItem>
              <MenuItem value="west">West</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="asset-class-label">Asset Class</InputLabel>
            <Select
              labelId="asset-class-label"
              id="asset-class"
              value={assetClass}
              label="Asset Class"
              onChange={handleAssetClassChange}
            >
              <MenuItem value="all">All Classes</MenuItem>
              <MenuItem value="office">Office</MenuItem>
              <MenuItem value="retail">Retail</MenuItem>
              <MenuItem value="industrial">Industrial</MenuItem>
              <MenuItem value="multifamily">Multifamily</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<FilterListIcon />}
            sx={{ ml: { xs: 0, md: 'auto' } }}
          >
            Apply Filters
          </Button>
        </Box>
      </Paper>

      {/* Key Metrics Cards */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Key Metrics</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
          {mockKeyMetrics.map((metric) => (
            <Card key={metric.id} variant="elevated" sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1 }}>
              <Card.Header
                title={metric.name}
                sx={{ paddingBottom: 0 }}
              />
              <Card.Content>
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                  <Typography variant="h4" component="div">
                    {metric.unit === '$' ? `$${metric.value}` : metric.value}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
                    {metric.unit !== '$' && metric.unit}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTrendIcon(metric.trend || 0)}
                  <Typography
                    variant="body2"
                    sx={{ ml: 0.5, color: getTrendColor(metric.trend || 0) }}
                  >
                    {metric.trend ? formatTrend(metric.trend) : 'No change'}
                  </Typography>
                </Box>
              </Card.Content>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Performance Charts</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
          {/* Rent Roll Projection Chart */}
          <Card variant="default" sx={{ flex: 2 }}>
            <Card.Header
              title="Rent Roll Projection"
              action={
                <Tooltip title="More options">
                  <IconButton size="small">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <Card.Content>
              <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={mockChartData[0].data}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis tickFormatter={(value) => `$${value / 1000000}M`}>
                  <Label
                    value="Revenue (Millions)"
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: 'middle' }}
                  />
                </YAxis>
                <RechartsTooltip 
                  formatter={(value: number) => [`$${formatNumber(value)}`, 'Revenue']} 
                  labelFormatter={(label) => `Year: ${label}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  name="Annual Rent" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                />
              </LineChart>
              </ResponsiveContainer>
            </Card.Content>
          </Card>

          {/* GLA by Tenant Type Chart */}
          <Card variant="default" sx={{ flex: 1 }}>
            <Card.Header
              title="GLA by Tenant Type"
              action={
                <Tooltip title="More options">
                  <IconButton size="small">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <Card.Content>
              <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mockChartData[1].data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: { name?: string, percent?: number }) =>
                    `${name || ''}: ${percent ? (percent * 100).toFixed(0) : 0}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                >
                  {mockChartData[1].data.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value: number) => [`${formatNumber(value)} SF`, 'GLA']} 
                />
                <Legend />
              </PieChart>
              </ResponsiveContainer>
            </Card.Content>
          </Card>
        </Box>
      </Box>

      {/* LTV Ladder Chart */}
      <Card variant="default" sx={{ mb: 3 }}>
        <Card.Header
          title="LTV Ladder"
          action={
            <Tooltip title="More options">
              <IconButton size="small">
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
        <Card.Content>
          <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={mockChartData[2].data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis domain={[0, 100]}>
              <Label
                value="LTV (%)"
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: 'middle' }}
              />
            </YAxis>
            <RechartsTooltip 
              formatter={(value: number) => [`${value}%`, 'LTV']} 
              labelFormatter={(label) => `Year: ${label}`}
            />
            <Legend />
            <Bar dataKey="value" name="LTV Percentage" fill="#82ca9d" />
          </BarChart>
          </ResponsiveContainer>
        </Card.Content>
      </Card>
    </Box>
  );
};

export default Dashboard;
